import { useTranslation } from 'next-i18next';
import * as yup from 'yup';

import { useApprovals } from '@/modules/settings/approvals/components/shared/logic';
import { IApprovals } from '@/modules/settings/approvals/types';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';

import Clipboard from '~/images/shared/Clipboard.svg';
import Location from '~/images/shared/Location.svg';
import Shovel from '~/images/shared/Shovel.svg';

export const useInventoryApprovals = () => {
  const { t } = useTranslation();

  const getFieldsOptions = (
    list: IApprovals.IGroupedPlots[] | undefined
  ): IApprovals.IGroupBaseWithImage[] => {
    return (
      list?.map((item) => {
        return {
          label: item.name,
          image: item.image as string,
          options: item.plots.map((plot) => {
            return {
              name: plot.name,
              id: plot.id,
              image: item.image,
            };
          }),
        };
      }) || []
    );
  };

  const { addApproverSchema } = useApprovals();

  const addInventoryApprovalPathSchema: yup.SchemaOf<IApprovals.ICreateInventoryApprovalForm> =
    yup.object().shape({
      name: yup
        .string()
        .required(t('this-field-is-required'))
        .matches(
          //  TODO: create util for this regex and other validation functions
          /^[a-zA-Z0-9\p{L}\p{N}\s]+$/u,
          t('this-field-is-char-and-nums-only')
        )
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        ),
      notes: yup
        .string()
        .notRequired()
        .max(
          500,
          t('this-field-not-accept-more-than-x-character', { count: 500 })
        ),
      entityId: yup.number().required(t('this-field-is-required')),
      approvalCycleCategories: yup
        .array()
        .of(yup.number().required(t('this-field-is-required')))
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      approvalCyclePlots: yup
        .array()
        .of(yup.number().required(t('this-field-is-required')))
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      approvers: yup
        .array()
        .of(addApproverSchema.required(t('this-field-is-required')))
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
    });

  const getAddApprovalMutation = (
    data: IApprovals.ICreateInventoryApprovalForm
  ): IApprovals.ICreateInventoryApprovalDTO => {
    const { approvers, ...rest } = data;
    return {
      approvers: approvers.map((approver, idx) => ({
        approverId: approver.approverId,
        editAccess: Boolean(approver.canEdit),
        stepOrder: idx + 1,
      })),
      ...rest,
    };
  };

  const getUpdateInventoryApprovalMutation = (
    data: IApprovals.IUpdateApproverSchema
  ): IApprovals.IUpdateInventoryApprovalParams => {
    return {
      approvalCycleId: data.approvalPathId,
      entityId: data.entityId,
      approvals: data.approvers.map((item, idx) => ({
        editAccess: Boolean(item.canEdit),
        stepOrder: idx + 1,
        approverId: item.approverId,
      })),
    };
  };

  const getCardDetils = (
    data: IApprovals.IInventoryApprovalCycle
  ): IIconWithTitleDescriptionProps[] => {
    return [
      {
        icon: <Location width='24' height='24' />,
        title: t('location'),
        description: data.entityName,
      },
      {
        icon: <Location width='24' height='24' />,
        title: t('work-locations'),
        description: (
          <PartialListWithTooltip
            limit={2}
            items={data.plots.map((plot) => ({
              description: plot.plotName,
              imageURL: plot.cropImage,
            }))}
          />
        ),
      },
      {
        icon: <Shovel width='24' height='24' />,
        title: t('product-type'),
        description: (
          <PartialListWithTooltip
            limit={2}
            items={data.categories.map((category) => ({
              description: category.categoryName,
            }))}
          />
        ),
      },
      {
        icon: <Clipboard width='24' height='24' />,
        title: t('notes'),
        description: data.approvalCycleNotes || '-',
      },
    ];
  };

  return {
    getFieldsOptions,
    addInventoryApprovalPathSchema,
    getAddApprovalMutation,
    getUpdateInventoryApprovalMutation,
    getCardDetils,
  };
};
